import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-draw';

const DRAW_COLORS = {
  distance: '#38bdf8',
  area: '#fb7185',
  draw: '#34d399'
};

const formatDistance = (meters) => {
  if (!Number.isFinite(meters)) return '';
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} كم`;
  }
  return `${Math.round(meters)} م`;
};

const formatArea = (squareMeters) => {
  if (!Number.isFinite(squareMeters)) return '';
  if (squareMeters >= 1000000) {
    return `${(squareMeters / 1000000).toFixed(2)} كم²`;
  }
  return `${Math.round(squareMeters)} م²`;
};

const getPrimaryRing = (latlngs) => {
  if (!Array.isArray(latlngs)) return [];
  if (Array.isArray(latlngs[0])) {
    return latlngs[0];
  }
  return latlngs;
};

const measurePolyline = (map, latlngs) => {
  if (!map || latlngs.length < 2) return '';
  const total = latlngs.reduce((acc, point, index) => {
    if (index === 0) return acc;
    return acc + map.distance(latlngs[index - 1], point);
  }, 0);
  return formatDistance(total);
};

const measurePolygon = (latlngs) => {
  const ring = getPrimaryRing(latlngs);
  if (!ring || ring.length < 3) return '';
  if (L.GeometryUtil?.geodesicArea) {
    return formatArea(L.GeometryUtil.geodesicArea(ring));
  }
  return '';
};

export const DrawControl = ({ activeTool, clearSignal }) => {
  const map = useMap();
  const drawnItemsRef = useRef(null);
  const drawControlRef = useRef(null);
  const activeDrawerRef = useRef(null);

  useEffect(() => {
    if (!map) return undefined;

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;

    const control = new L.Control.Draw({
      position: 'topleft',
      draw: false,
      edit: {
        featureGroup: drawnItems,
        edit: {
          selectedPathOptions: {
            color: '#a78bfa',
            fillOpacity: 0.24
          }
        },
        remove: true
      }
    });
    map.addControl(control);
    drawControlRef.current = control;

    const handleCreated = (event) => {
      const { layer, layerType } = event;
      drawnItems.addLayer(layer);

      if (layerType === 'polyline') {
        const length = measurePolyline(map, layer.getLatLngs());
        if (length) {
          layer.bindTooltip(`الطول: ${length}`, { sticky: true, direction: 'top' }).openTooltip();
        }
      }

      if (layerType === 'polygon' || layerType === 'rectangle') {
        const area = measurePolygon(layer.getLatLngs());
        if (area) {
          layer.bindTooltip(`المساحة: ${area}`, { sticky: true, direction: 'top' }).openTooltip();
        }
      }
    };

    map.on(L.Draw.Event.CREATED, handleCreated);

    return () => {
      map.off(L.Draw.Event.CREATED, handleCreated);
      if (drawControlRef.current) {
        map.removeControl(drawControlRef.current);
      }
      if (drawnItemsRef.current) {
        map.removeLayer(drawnItemsRef.current);
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;

    if (activeDrawerRef.current) {
      activeDrawerRef.current.disable();
      activeDrawerRef.current = null;
    }

    if (!activeTool) return;

    const shapeOptions = {
      color: DRAW_COLORS[activeTool] || '#34d399',
      weight: 3,
      fillOpacity: 0.24
    };

    let drawer = null;
    if (activeTool === 'distance') {
      drawer = new L.Draw.Polyline(map, { shapeOptions, repeatMode: true, metric: true });
    }
    if (activeTool === 'area') {
      drawer = new L.Draw.Polygon(map, { shapeOptions, repeatMode: true, showArea: true, metric: true });
    }
    if (activeTool === 'draw') {
      drawer = new L.Draw.Rectangle(map, { shapeOptions, repeatMode: true });
    }

    if (drawer) {
      activeDrawerRef.current = drawer;
      drawer.enable();
    }
  }, [activeTool, map]);

  useEffect(() => {
    if (!drawnItemsRef.current) return;
    drawnItemsRef.current.clearLayers();
  }, [clearSignal]);

  return null;
};
